import type {ErrorLogRef, Instant, UUID} from './common'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import type {useI18n} from '@/i18n'

type I18n = ReturnType<typeof useI18n>

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

// ============================================================================
// Enums
// ============================================================================

export const HandlerType = {
    BEAN_METHOD: 'BEAN_METHOD',
    GROOVY_SCRIPT: 'GROOVY_SCRIPT',
    DELAY: 'DELAY',
} as const;
export type HandlerType = typeof HandlerType[keyof typeof HandlerType]

export const JobSource = {
    STATIC: 'STATIC',
    TESTDATA: 'TESTDATA',
    DYNAMIC: 'DYNAMIC',
} as const;
export type JobSource = typeof JobSource[keyof typeof JobSource]

/**
 * Rich enum carrying UI rendering metadata (badge variant) for
 * {@link JobSource}. Label resolution lives in the i18n-backed
 * {@code useJobSourceLabel} hook.
 */
export class JobSourceBadge extends EnumValue {
    static readonly STATIC = new JobSourceBadge(JobSource.STATIC, BadgeVariant.DEFAULT)
    static readonly DYNAMIC = new JobSourceBadge(JobSource.DYNAMIC, BadgeVariant.SECONDARY)
    static readonly TESTDATA = new JobSourceBadge(JobSource.TESTDATA, BadgeVariant.OUTLINE)

    static readonly values: ReadonlyArray<JobSourceBadge> = [
        JobSourceBadge.STATIC,
        JobSourceBadge.DYNAMIC,
        JobSourceBadge.TESTDATA,
    ]

    static of(code: JobSource | null | undefined): JobSourceBadge | undefined {
        return JobSourceBadge.values.find((v) => v.code === code)
    }
}

export const JobExecutionStatus = {
    PENDING: 'PENDING',
    RUNNING: 'RUNNING',
    CANCELLING: 'CANCELLING',
    COMPLETED: 'COMPLETED',
    COMPLETED_WITH_ERRORS: 'COMPLETED_WITH_ERRORS',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
} as const;
export type JobExecutionStatus = typeof JobExecutionStatus[keyof typeof JobExecutionStatus]

export const TaskExecutionStatus = {
    PENDING: 'PENDING',
    WAITING: 'WAITING',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    COMPLETED_WITH_ERRORS: 'COMPLETED_WITH_ERRORS',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
} as const;
export type TaskExecutionStatus = typeof TaskExecutionStatus[keyof typeof TaskExecutionStatus]

export const LogLevel = {
    DEBUG: 'DEBUG',
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
} as const;
export type LogLevel = typeof LogLevel[keyof typeof LogLevel]

export const ExecutionType = {
    JOB_TASK: 'JOB_TASK',
} as const;
export type ExecutionType = typeof ExecutionType[keyof typeof ExecutionType]

// Legacy types — kept for page compatibility until 091.09 rewrites the pages
export const TaskType = {
    TASK: 'TASK',
    GROUP: 'GROUP',
    PARAMETERIZED: 'PARAMETERIZED',
} as const;
export type TaskType = typeof TaskType[keyof typeof TaskType]

export const ExecutionMode = {
    SEQUENTIAL: 'SEQUENTIAL',
    PARALLEL: 'PARALLEL',
} as const;
export type ExecutionMode = typeof ExecutionMode[keyof typeof ExecutionMode]

export const ClusterNodeStatus = {
    STARTING: 'STARTING',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    STOPPED: 'STOPPED',
} as const;
export type ClusterNodeStatus = typeof ClusterNodeStatus[keyof typeof ClusterNodeStatus]

// ============================================================================
// Cluster Node
// ============================================================================

export interface ClusterNode {
    nodeId: UUID
    hostname: string
    ipAddress: string
    status: ClusterNodeStatus
    master: boolean
    registeredAt: string
    lastHeartbeat: string
    metadata: Record<string, unknown>
}

// ============================================================================
// Job Definition (matches JobDefinitionController.JobDefinitionDto)
// ============================================================================

export interface JobDefinition {
    id: UUID
    jobKey: string
    name: string
    description: string | null
    cronExpression: string
    enabled: boolean
    timeoutMs: number
    retryCount: number
    maxConcurrent: number
    allowOverlap: boolean
    assignedNodeId: string | null
    taskCount: number
    nextRunTime: Instant | null
    lastExecutionStatus: JobExecutionStatus | null
    lastExecutionTime: Instant | null
    source: JobSource | null
    version: number
}

// ============================================================================
// Job Execution (matches JobExecutionController DTOs)
// ============================================================================

export interface JobExecution {
    id: UUID
    jobId: UUID
    nodeId: string
    status: JobExecutionStatus
    startedAt: string
    completedAt: string | null
    durationMs: number | null
    errorMessage: string | null
    stats: Record<string, unknown> | null
    progress: number | null
    errorLogRef: ErrorLogRef | null
}

export interface JobExecutionListItem {
    id: UUID
    jobId: UUID
    jobName: string
    jobKey: string | null
    nodeId: string
    status: JobExecutionStatus
    startedAt: string
    completedAt: string | null
    durationMs: number | null
    errorMessage: string | null
    totalTasks: number
    completedTasks: number
    failedTasks: number
    progress: number | null
    errorLogRef: ErrorLogRef | null
    stats?: Record<string, unknown>
}

// ============================================================================
// Task Definition (matches JobBuilderController.TaskDefinitionDto)
// ============================================================================

export interface TaskDefinition {
    id: UUID
    jobId: UUID
    name: string
    handlerType: string
    sortOrder: number
    timeoutMs: number | null
    retryCount: number
    logLevel: string
    handlerConfig: Record<string, unknown> | null
    skipOnFailure: boolean
    category: string | null
    source: string | null
    enabled: boolean
    parentName: string | null
    taskType: string | null
    executionMode: string | null
}

// ============================================================================
// Task Execution (matches JobExecutionController.TaskExecutionDto)
// ============================================================================

export interface TaskExecution {
    id: UUID
    executionId: UUID
    taskDefId: UUID
    taskInstanceKey: string
    nodeId: string | null
    status: TaskExecutionStatus
    startedAt: string
    completedAt: string | null
    durationMs: number | null
    errorMessage: string | null
    result: Record<string, unknown> | null
    progress: number
    lastHeartbeat: string | null
    retryCount: number
    taskName: string | null
    handlerType: string | null
    sortOrder: number
    skipOnFailure: boolean
    itemsTotal: number | null
    itemsSucceeded: number | null
    itemsFailed: number | null
    errorLogRef: ErrorLogRef | null
}

// Legacy tree item — used by ExecutionDetail/JobExecutions until 091.09 rewrite
export interface TaskExecutionTreeItem extends TaskExecution {
    taskType: TaskType
    executionMode: ExecutionMode | null
    parentName: string | null
    children: TaskExecutionTreeItem[]
    completedCount: number
    failedCount: number
    runningCount: number
    pendingCount: number
    skippedCount: number
}

// ============================================================================
// Job Builder (matches JobBuilderController DTOs)
// ============================================================================

export interface JobBuilderData {
    id: UUID
    jobKey: string
    name: string
    description: string | null
    cronExpression: string
    timeoutMs: number
    maxConcurrent: number
    allowOverlap: boolean
    version: number
    tasks: CanvasTask[]
}

export interface CanvasTask {
    id: string
    name: string
    handlerType: string
    sortOrder: number
    handlerConfig: Record<string, unknown>
    skipOnFailure: boolean
    timeoutMs?: number
    retryCount?: number
    category?: string
}

export const JobPriority = {
    CRITICAL: 'CRITICAL',
    HIGH: 'HIGH',
    NORMAL: 'NORMAL',
    LOW: 'LOW',
} as const;
export type JobPriority = typeof JobPriority[keyof typeof JobPriority]

/**
 * Rich enum for {@link JobPriority} carrying badge variants and i18n labels.
 * Variant mapping (per issue 389): CRITICAL → destructive, HIGH → warning,
 * NORMAL → default, LOW → outline.
 */
export class JobPriorityBadge extends EnumValue {
    static readonly CRITICAL = new JobPriorityBadge(
        JobPriority.CRITICAL,
        BadgeVariant.DESTRUCTIVE,
        (i18n: I18n) => i18n.jobBuilder.priorityCritical as string,
    )
    static readonly HIGH = new JobPriorityBadge(
        JobPriority.HIGH,
        BadgeVariant.WARNING,
        (i18n: I18n) => i18n.jobBuilder.priorityHigh as string,
    )
    static readonly NORMAL = new JobPriorityBadge(
        JobPriority.NORMAL,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.jobBuilder.priorityNormal as string,
    )
    static readonly LOW = new JobPriorityBadge(
        JobPriority.LOW,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.jobBuilder.priorityLow as string,
    )

    static readonly values: ReadonlyArray<JobPriorityBadge> = [
        JobPriorityBadge.CRITICAL,
        JobPriorityBadge.HIGH,
        JobPriorityBadge.NORMAL,
        JobPriorityBadge.LOW,
    ]

    static of(code: JobPriority | null | undefined): JobPriorityBadge | undefined {
        return JobPriorityBadge.values.find((v) => v.code === code)
    }
}

export interface TaskPaletteItem {
    id: string
    name: string
    handlerType: HandlerType
    category: string | null
    handlerConfig: Record<string, unknown>
    source: string | null
}

export interface CreateJobRequest {
    jobKey: string
    name: string
    description?: string
    cronExpression: string
    timeoutMs?: number
    maxConcurrent?: number
    allowOverlap?: boolean
    tasks: TaskRequest[]
}

export interface TaskRequest {
    name: string
    sortOrder?: number
    handlerType?: string
    handlerConfig?: Record<string, unknown>
    timeoutMs?: number
    retryCount?: number
    skipOnFailure?: boolean
    category?: string
}

// ============================================================================
// Job Stats (matches JobDefinitionController.JobStatsDto)
// ============================================================================

export interface JobStats {
    completed: number
    failed: number
    running: number
    cancelled: number
    pending: number
    total: number
    avgDurationMs: number
    totalJobs: number
    enabledJobs: number
    from: string
    to: string
}

// ============================================================================
// Execution Logs
// ============================================================================

export interface ExecutionLog {
    id: UUID
    executionType: ExecutionType
    referenceId: UUID
    logLevel: LogLevel
    message: string
    context: Record<string, unknown> | null
    createdAt: Instant
}

// ============================================================================
// SSE Event types
// ============================================================================

export enum ExecutionEventType {
    CONNECTED = 'CONNECTED',
    JOB_STARTED = 'JOB_STARTED',
    JOB_PROGRESS = 'JOB_PROGRESS',
    JOB_COMPLETED = 'JOB_COMPLETED',
    JOB_FAILED = 'JOB_FAILED',
    JOB_CANCELLED = 'JOB_CANCELLED',
    TASK_STARTED = 'TASK_STARTED',
    TASK_PROGRESS = 'TASK_PROGRESS',
    TASK_COMPLETED = 'TASK_COMPLETED',
    TASK_FAILED = 'TASK_FAILED',
    TASK_SKIPPED = 'TASK_SKIPPED',
}

export function toSseEventName(type: ExecutionEventType): string {
    return type.toLowerCase()
}

export const JOB_EVENT_TYPES: readonly ExecutionEventType[] = [
    ExecutionEventType.CONNECTED,
    ExecutionEventType.JOB_STARTED,
    ExecutionEventType.JOB_PROGRESS,
    ExecutionEventType.JOB_COMPLETED,
    ExecutionEventType.JOB_FAILED,
    ExecutionEventType.JOB_CANCELLED,
]

export const ALL_EVENT_TYPES: readonly ExecutionEventType[] = Object.values(ExecutionEventType)

export interface ExecutionStreamEvent {
    executionId: UUID
    eventType: ExecutionEventType
    taskId: UUID | null
    status: string
    progress: number | null
    errorMessage: string | null
    timestamp: number
}

// ============================================================================
// Job Graph Preview
// ============================================================================

export interface JobGraphPreview {
    jobId: UUID
    jobKey: string
    jobName: string
    nodes: TaskNode[]
    edges: TaskEdge[]
}

export interface TaskNode {
    id: UUID
    name: string
    taskType: string
    parentName: string | null
    sortOrder: number
}

export interface TaskEdge {
    from: string
    to: string
    type: 'parent'
}

// ============================================================================
// Config Validation
// ============================================================================

export interface ConfigValidationResponse {
    valid: boolean
    errors: string[]
}

// ============================================================================
// Reporting (unrelated to jobs/scripts — kept here for now)
// ============================================================================

export interface ReportDefinitionListItem {
    reportDefinitionId: UUID
    name: string
    description: string | null
    categoryName: string
    categoryId: UUID
    updatedAt: Instant
    lastRunAt: Instant | null
    version: number
}

export interface ReportDefinitionDetail extends ReportDefinitionListItem {
    queryDefinition: {sql: string}
    parameters: import('@/types/attribute-definition').AttributeDefinition[]
    retention: string
    ownerAuthUserId: UUID | null
    createdAt: Instant
    scheduleCron: string | null
    recipientIdentityIds: UUID[]
    recipientEmails: string[]
    notificationTemplateKey: string | null
}

/* eslint-disable i18next/no-literal-string */
export const CategoryType = {
    GLOBAL: 'GLOBAL',
    PERSONAL: 'PERSONAL',
} as const
export type CategoryType = typeof CategoryType[keyof typeof CategoryType]
/* eslint-enable i18next/no-literal-string */

export interface ReportCategoryListItem {
    categoryId: UUID
    name: string
    description: string | null
    type: CategoryType
    reportCount: number
    updatedAt: Instant
    parentCategoryId: UUID | null
    version: number
}

export interface ReportCategoryDetail extends ReportCategoryListItem {
    ownerAuthUserId: UUID | null
    createdAt: Instant
}

export interface SqlAssist {
    suggestion: string
}

// ============================================================================
// Failure Records (matches ErrorLogController responses)
// ============================================================================

export interface CharChange {
    start: number
    end: number
    templateText: string
    actualText: string
}

export interface LineDiffInfo {
    lineNumber: number
    changes: CharChange[]
}

export interface ErrorLog {
    errorLogId: UUID
    templateRecordId: UUID | null
    simhash: number
    sourceModule: string
    sourceType: string
    sourceId: UUID | null
    correlationId: UUID | null
    exceptionType: string
    appErrorCode: string | null
    message: string
    stackTrace: string | null
    diffMarkers: string[] | null
    lineDiffs: LineDiffInfo[] | null
    context: Record<string, unknown> | null
    occurrenceCount: number
    lastSeenAt: Instant | null
    createdAt: Instant
    isTemplate: boolean
    requestMethod?: string | null
    requestUrl?: string | null
    userAgent?: string | null
    requestCorrelationId?: string | null
    requestHeaders?: Record<string, string> | null
    authUsername?: string | null
    sessionId?: string | null
    actionId?: string | null
    e2eRunId?: string | null
    e2eTestClass?: string | null
    e2eTestMethod?: string | null
}

export interface ErrorLogStats {
    totalPatterns: number
    totalOccurrences: number
    countByModule: Record<string, number>
    period: string
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const ErrorLogResponseFields = {
    errorLogId: 'errorLogId',
    templateRecordId: 'templateRecordId',
    simhash: 'simhash',
    sourceModule: 'sourceModule',
    sourceType: 'sourceType',
    sourceId: 'sourceId',
    correlationId: 'correlationId',
    exceptionType: 'exceptionType',
    appErrorCode: 'appErrorCode',
    message: 'message',
    stackTrace: 'stackTrace',
    diffMarkers: 'diffMarkers',
    lineDiffs: 'lineDiffs',
    context: 'context',
    occurrenceCount: 'occurrenceCount',
    lastSeenAt: 'lastSeenAt',
    createdAt: 'createdAt',
    isTemplate: 'isTemplate',
    requestMethod: 'requestMethod',
    requestUrl: 'requestUrl',
    userAgent: 'userAgent',
    requestCorrelationId: 'requestCorrelationId',
    requestHeaders: 'requestHeaders',
    authUsername: 'authUsername',
    sessionId: 'sessionId',
    actionId: 'actionId',
    e2eRunId: 'e2eRunId',
    e2eTestClass: 'e2eTestClass',
    e2eTestMethod: 'e2eTestMethod',
} as const

export const ErrorLogStatsResponseFields = {
    totalPatterns: 'totalPatterns',
    totalOccurrences: 'totalOccurrences',
    countByModule: 'countByModule',
    period: 'period',
} as const

export const JobExecutionFilterParams = {
    status: 'status',
    jobId: 'jobId',
} as const

export const ErrorLogFilterParams = {
    sourceType: 'sourceType',
    period: 'period',
    page: 'page',
    size: 'size',
} as const
/* eslint-enable i18next/no-literal-string */
